export const MAX_BLOCKED_URLS = 200

/**
 * Reduce a user-entered URL/host to the canonical host the backend stores
 * (lowercase hostname, no scheme/path/port/query, no leading `www.`/trailing dot;
 * `new URL().hostname` punycodes IDNs). Mirrors the aria-web backend
 * `_canonicalize_host` (the source of truth) and is UX/preview-only. Returns
 * `null` for blank/unparseable/single-label input.
 */
export function canonicalizeHost(raw: string): string | null {
  const text = raw.trim()
  if (!text) return null
  const withScheme = text.includes('://') ? text : `https://${text}`
  let host: string
  try {
    host = new URL(withScheme).hostname.toLowerCase()
  } catch {
    return null
  }
  host = host.replace(/\.$/, '')
  if (host.startsWith('www.')) host = host.slice('www.'.length)
  if (!host || !host.includes('.')) return null
  return host
}

export interface ParsedBlockedUrls {
  /** Canonical hosts, de-duplicated, in first-seen order. */
  hosts: string[]
  /** Raw tokens that could not be parsed into a host. */
  invalid: string[]
}

/**
 * Split a comma/newline-separated textarea into canonical hosts. Blank cells are
 * dropped silently; unparseable tokens are collected so the UI can name them.
 */
export function parseBlockedUrls(input: string): ParsedBlockedUrls {
  const seen = new Set<string>()
  const hosts: string[] = []
  const invalid: string[] = []
  for (const token of input.split(/[\n,]/)) {
    const trimmed = token.trim()
    if (!trimmed) continue
    const host = canonicalizeHost(trimmed)
    if (!host) {
      invalid.push(trimmed)
      continue
    }
    if (!seen.has(host)) {
      seen.add(host)
      hosts.push(host)
    }
  }
  return { hosts, invalid }
}

/** Render a canonical host list back into textarea text (one per line). */
export function hostsToText(hosts: string[]): string {
  return hosts.join('\n')
}

/** Order-independent equality of two host lists (already canonical). */
export function hostListsEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  const setB = new Set(b)
  return a.every((host) => setB.has(host))
}
